import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getAllBranch, setPurchaseOrdersBranch } from "../../services";
import { Branch } from "../../types/user.type";
import { PurchaseOrderListItem } from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { countPublishedLeaving, partitionBranchAssignable } from "./data";

/** ค่าว่างของ dropdown — ยังไม่เลือกสาขา ปุ่มบันทึกจึงกดไม่ได้ */
const NO_BRANCH_CHOSEN = "";

interface SetBranchDialogProps {
  open: boolean;
  /**
   * ใบที่ติ๊กเลือกไว้ · การเลือกจำกัดอยู่ที่หน้าเดียว (สูงสุด 100 ใบ) จึงไม่มีทางเกินเพดาน
   * 500 uuid ของ API · ใบที่ใช้แล้ว/หมดช่วงใช้ถูกตัดออกในนี้ ไม่ถูกส่งไปให้ 409 ทั้งคำขอ
   */
  purchaseOrders: PurchaseOrderListItem[];
  onClose: () => void;
  onUpdated: () => void;
}

/**
 * W1 ระบุ/ย้ายสาขา — ชี้สาขาให้ PO ที่ยังไม่มีสาขา ที่สาขาส่งคืน หรือย้ายใบที่ส่งไปแล้ว
 *
 * สองทางออก: "บันทึก" (ยังไม่ส่ง) และ "บันทึกและส่งสาขา" (publish ในคำขอเดียว) · ปุ่มหลัก
 * คือส่งสาขาเมื่อมีใบที่ยังไม่ระบุสาขาหรือสาขาส่งคืน เพราะงานของ HO กับใบพวกนั้นจบที่การส่ง
 * · ย้ายใบที่ส่งไปแล้วไปสาขาอื่น = ดึงกลับจากสาขาเดิมและล้างสถานะส่งคืน จึงเตือนก่อนกด
 * ส่วนการเลือกสาขาเดิมไม่ดึงกลับและไม่ล้างสถานะส่งคืน (ตามกติกาของ backend)
 */
export default function SetBranchDialog({
  open,
  purchaseOrders,
  onClose,
  onUpdated,
}: SetBranchDialogProps) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyId, setCompanyId] = useState<string>(NO_BRANCH_CHOSEN);
  const [includeSameStore, setIncludeSameStore] = useState<boolean>(true);
  // ปุ่มที่กำลังส่งคำขอ — หมุนเฉพาะปุ่มนั้น และล็อกทุกทางออกระหว่างรอ
  const [savingMode, setSavingMode] = useState<"save" | "publish" | null>(
    null,
  );

  const partition = useMemo(
    () => partitionBranchAssignable(purchaseOrders),
    [purchaseOrders],
  );
  const { assignable, excluded, prefersPublish } = partition;
  const publishedLeavingCount = countPublishedLeaving(assignable, companyId);
  const hasNoBranch = assignable.some((row) => row.status === "NO_BRANCH");

  const loadBranches = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getAllBranch();
      setBranches(data ?? []);
    } catch {
      // interceptor แสดง error ให้แล้ว
    }
  }, []);

  // ร่างของ dialog เริ่มใหม่ทุกครั้งที่เปิด · อ่านรายการสาขาครั้งแรกที่เปิดเท่านั้น
  useEffect(() => {
    if (!open) return;
    setCompanyId(NO_BRANCH_CHOSEN);
    setIncludeSameStore(true);
    if (branches.length === 0) loadBranches();
  }, [open, branches.length, loadBranches]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setCompanyId(event.target.value);
  };

  const handleToggleSameStore = (event: ChangeEvent<HTMLInputElement>) => {
    setIncludeSameStore(event.target.checked);
  };

  const submit = async (publish: boolean) => {
    if (companyId === NO_BRANCH_CHOSEN || assignable.length === 0) return;

    setSavingMode(publish ? "publish" : "save");
    try {
      const {
        data: { data },
      } = await setPurchaseOrdersBranch({
        uuids: assignable.map((row) => row.uuid),
        company_id: companyId,
        include_same_store: hasNoBranch ? includeSameStore : undefined,
        publish: publish || undefined,
      });

      if (data) {
        pushGlobalSuccess(
          `ระบุสาขาแล้ว ${numberWithCommas(data.updated_count)} ใบ` +
            ` · ผูกร้านให้แล้ว ${numberWithCommas(data.matched_count)} ใบ` +
            (publish
              ? ` · ส่งให้สาขาแล้ว ${numberWithCommas(data.published_count ?? 0)} ใบ`
              : ""),
        );
      }
      onUpdated();
      onClose();
    } catch {
      // 409 (มีใบที่ใช้แล้ว/หมดช่วงใช้) / 404 (ไม่รู้จักสาขา) ถูกแจ้งโดย interceptor
      // ด้วยข้อความจาก API แล้ว · คง dialog ไว้ให้ผู้ใช้ปิดแล้วเลือกใหม่
    } finally {
      setSavingMode(null);
    }
  };

  const busy = savingMode !== null;
  const cannotSubmit =
    busy || companyId === NO_BRANCH_CHOSEN || assignable.length === 0;
  const storeCount = new Set(assignable.map((row) => row.ship_to_code)).size;

  const saveButton = (
    <Button
      variant={prefersPublish ? "outlined" : "contained"}
      loading={savingMode === "save"}
      disabled={cannotSubmit}
      onClick={() => submit(false)}
    >
      บันทึก
    </Button>
  );
  const publishButton = (
    <Button
      variant={prefersPublish ? "contained" : "outlined"}
      loading={savingMode === "publish"}
      disabled={cannotSubmit}
      onClick={() => submit(true)}
    >
      บันทึกและส่งสาขา
    </Button>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose}>
      <DialogTitle>ระบุ / ย้ายสาขา</DialogTitle>
      <DialogContent>
        <DialogContentText>
          PO {numberWithCommas(assignable.length)} ใบ (
          {numberWithCommas(storeCount)} store number)
        </DialogContentText>

        <Stack mt={2} gap={1.5}>
          {excluded.length > 0 && (
            <Alert severity="info">
              ไม่รวม{" "}
              {excluded
                .map(
                  ({ reason, count }) =>
                    `${numberWithCommas(count)} ใบที่${reason}`,
                )
                .join(" และ ")}{" "}
              — ย้ายสาขาไม่ได้แล้ว
            </Alert>
          )}

          {publishedLeavingCount > 0 && (
            <Alert severity="warning">
              {numberWithCommas(publishedLeavingCount)} ใบส่งให้สาขาแล้ว —
              จะถูกดึงกลับจากสาขาเดิม
              {companyId === NO_BRANCH_CHOSEN
                ? " ถ้าย้ายไปสาขาอื่น (ย้ายไปสาขาเดิมยังคงส่งอยู่)"
                : ""}
            </Alert>
          )}

          <TextField
            select
            size="small"
            fullWidth
            label="สาขา"
            value={companyId}
            onChange={handleChangeBranch}
          >
            <MenuItem value={NO_BRANCH_CHOSEN}>เลือกสาขา</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.company_id}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>

          {/* ขยายไปใบอื่นได้เฉพาะใบที่ยังไม่ระบุสาขา backend จึงรับค่านี้เมื่อมีใบกลุ่มนั้น */}
          {hasNoBranch && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeSameStore}
                  onChange={handleToggleSameStore}
                />
              }
              label="ใช้กับ PO อื่นที่ store number เดียวกันที่ยังไม่ระบุสาขา"
            />
          )}

          <Typography variant="body2" color="text.secondary">
            ถ้าสาขาที่เลือกมีร้านที่ถือ store number นี้อยู่ร้านเดียว ระบบผูกร้านให้เลย
            นอกนั้นสาขาจับคู่ร้านเองที่จอ PO ของสาขา · ย้ายไปสาขาอื่นจะล้างสถานะ
            สาขาส่งคืน ส่วนใบที่เลือกสาขาเดิมยังคงสถานะเดิม
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          ยกเลิก
        </Button>
        {/* ปุ่มหลักอยู่ขวาสุดเสมอ */}
        {prefersPublish ? saveButton : publishButton}
        {prefersPublish ? publishButton : saveButton}
      </DialogActions>
    </Dialog>
  );
}
